/**
 * <Skeleton> — un bloc qui tient la place d'un contenu en chargement (atom).
 *
 * Quatre formes, et chacune prend EXACTEMENT la place de ce qu'elle remplace, pour que l'arrivée
 * des données ne fasse rien sauter :
 *  - `text` : une ligne de texte. La boîte a la hauteur de la ligne d'un `<Text>` de la même
 *    taille ; la barre, plus basse, est posée au milieu — comme l'œil du texte dans sa ligne ;
 *  - `block` : un aplat — une tuile, un bouton, une image, une jauge ;
 *  - `chip` : une chip du livre (`<Badge>`) — sa hauteur, son rayon plein ; seule sa largeur varie ;
 *  - `circle` : un visage, une pastille, une icône ronde.
 *
 * Le bloc est peint aux jetons de surface (`initials-fill` : le bleu très clair des visages sans
 * photo, la surface surélevée sur la nuit) et ne dessine rien d'autre. Sous un `<SkeletonGroup>`,
 * le surligneur de la marque passe sur les lignes de texte : un trait très léger (jeton
 * `skeleton-sweep`), incliné comme `<Highlight>`, qui traverse la page de gauche à droite. En
 * clair, il est jaune et passe sous le bas de la barre — la place du surligneur sous un mot ; sur
 * la nuit, il est blanc et la couvre, comme `<Highlight>`.
 * Un visage, une tuile, une image ne se surlignent pas : ils restent fixes.
 *
 * Hors d'un `<SkeletonGroup>`, ou en mouvement réduit, le bloc reste fixe.
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Animated, View, type DimensionValue, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type RadiusKey } from "../../theme/tokens/radius"
import { fontSizes, type FontSizeKey } from "../../theme/tokens/typography"
import { CHIP_HEIGHT } from "../Badge/Badge"
import { SkeletonSweepContext } from "./SkeletonGroup"
import { skeletonSweep } from "./skeletonSweep"

export type SkeletonShape = "text" | "block" | "chip" | "circle"

export type SkeletonProps = {
    /** `text` par défaut. */
    shape?: SkeletonShape
    /** `text` : la taille du texte remplacé — `text-base` par défaut. */
    textSize?: FontSizeKey
    /**
     * `text`, `block` et `chip` : la largeur, en px ou en pourcentage de la ligne — `100%` par défaut. Un
     * texte court (un titre, un geste) se donne en px : un pourcentage d'une colonne de grand
     * écran le rendrait trois fois trop long. La barre ne dépasse jamais sa ligne.
     */
    width?: DimensionValue
    /** `block` : la hauteur, en px — 44 par défaut (un bouton). */
    height?: number
    /** `circle` : le diamètre, en px — 34 par défaut (l'avatar d'une ligne de séance). */
    size?: number
    /** `block` : le rayon — `md` par défaut (celui d'un bouton). */
    radius?: RadiusKey
    style?: StyleProp<ViewStyle>
    testID?: string
}

/**
 * La hauteur de ligne d'Outfit rapportée à sa taille : le `<Text>` du DS ne fixe pas de
 * `lineHeight`, le moteur prend celle de la fonte (ascendante 1 000 + descendante 260, pour 1 000).
 */
const LINE_BOX = 1.26

/** La hauteur de la ligne d'un `<Text>` de cette taille — ce qu'une boîte mesurée sur du texte réserve. */
export function textLineBox(size: FontSizeKey): number {
    return Math.round(fontSizes[size] * LINE_BOX)
}

/** La barre d'une ligne de texte, rapportée à la taille du texte : l'œil des lettres. */
const TEXT_BAR = 0.72

/** Ce que la bande déborde de sa barre en haut et en bas : l'inclinaison ne découvre aucun coin. */
const BAND_BLEED = 6

export function Skeleton(props: SkeletonProps) {
    const { shape = "text", textSize = "text-base", width = "100%", height = 44, size = 34, radius = "md" } = props
    const { style, testID } = props
    const sweep = useContext(SkeletonSweepContext)
    const bar = useRef<View>(null)
    // Le surligneur ne passe que sur les lignes de texte, et seulement quand il bouge : un visage,
    // une tuile, une image, ou tout bloc en mouvement réduit reste fixe — il n'a pas à se mesurer.
    // (Un aplat entier teinté de jaune virait au kaki sur le bleu du bloc.)
    const tracks = shape === "text" && sweep?.moving === true
    // Où la barre tombe dans la page : c'est ce qui fait passer le trait sur elle à son tour.
    const [offset, setOffset] = useState<number | null>(null)

    const host = sweep?.host
    const measure = useCallback(() => {
        const node = bar.current
        const page = host?.current
        if (node == null || page == null) return
        node.measureLayout(
            page,
            (left) => setOffset((previous) => (previous === left ? previous : left)),
            () => {}
        )
    }, [host])

    // La page change de largeur (rotation, fenêtre redimensionnée) : les blocs se remesurent.
    const pageWidth = sweep?.width ?? 0
    useEffect(() => {
        if (tracks && pageWidth > 0) measure()
    }, [tracks, pageWidth, measure])

    const band = useMemo(() => {
        if (!tracks || sweep == null || offset == null || sweep.width <= 0) return null
        return {
            length: sweep.length,
            translateX: sweep.progress.interpolate({
                inputRange: [0, 1],
                outputRange: [-sweep.length - offset, sweep.width * (1 + skeletonSweep.overshoot) - offset],
            }),
        }
    }, [tracks, sweep, offset])

    const underline = sweep != null && !sweep.night
    const alpha = sweep?.night ? skeletonSweep.alpha.night : skeletonSweep.alpha.day

    const barStyle =
        shape === "circle"
            ? styles.circle(size)
            : shape === "block"
              ? styles.block(width, height, radius)
              : shape === "chip"
                ? styles.block(width, CHIP_HEIGHT, "full")
                : styles.textBar(textSize)

    const bone = (
        <View
            ref={bar}
            onLayout={tracks ? measure : undefined}
            testID={shape === "text" ? undefined : testID}
            style={[styles.bone, barStyle, shape === "text" ? undefined : style]}
        >
            {band != null && (
                <Animated.View
                    pointerEvents="none"
                    style={[
                        styles.band(alpha, underline),
                        {
                            width: band.length,
                            transform: [{ translateX: band.translateX }, { rotate: `${skeletonSweep.rotate}deg` }],
                        },
                    ]}
                />
            )}
        </View>
    )

    if (shape !== "text") return bone

    return (
        <View testID={testID} style={[styles.line(textSize, width), style]}>
            {bone}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Le bloc : l'aplat de surface, et il rogne le trait qui passe dessus. */
    bone: {
        overflow: "hidden",
        backgroundColor: theme.colors["initials-fill"],
    },
    /** La boîte d'une ligne de texte : la hauteur de la ligne, la barre centrée dedans. */
    line: (textSize: FontSizeKey, width: DimensionValue) => ({
        width,
        maxWidth: "100%",
        height: Math.round(theme.fontSizes[textSize] * LINE_BOX),
        justifyContent: "center" as const,
    }),
    textBar: (textSize: FontSizeKey) => ({
        height: Math.round(theme.fontSizes[textSize] * TEXT_BAR),
        borderRadius: theme.radius.sm,
    }),
    block: (width: DimensionValue, height: number, radius: RadiusKey) => ({
        width,
        maxWidth: "100%",
        height,
        borderRadius: theme.radius[radius],
    }),
    circle: (size: number) => ({
        width: size,
        height: size,
        borderRadius: size / 2,
    }),
    /**
     * Le trait du surligneur, au jeton `skeleton-sweep` et à l'opacité de la recette. En clair, il
     * part du bas du mot (`top` du surligneur) ; sur la nuit, il couvre la barre et déborde un peu,
     * pour que l'inclinaison ne découvre aucun coin.
     */
    band: (alpha: number, underline: boolean) => ({
        position: "absolute" as const,
        left: 0,
        top: underline ? skeletonSweep.underlineTop : -BAND_BLEED,
        bottom: -BAND_BLEED,
        borderRadius: theme.radius.sm,
        backgroundColor: theme.colors["skeleton-sweep"],
        opacity: alpha,
    }),
}))
